import {
  queueSchema,
  queueStrategies,
  queueStrategyNames,
  type AgentLiveState,
  type AgentStatus,
  type CallbackSummary,
  type Me,
  type MyQueues,
  type QueueInput,
  type QueueLive,
  type QueueReport,
  type QueueSummary,
  type HoldMusicSummary,
  type RegionSummary,
  type UserSummary,
} from '@smarton-pbx/shared';
import { Check, Headset, ListChecks, MoreHorizontal, Pencil, PhoneOutgoing, Plus, RotateCcw, Trash2, Users, X } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api';
import { Dialog, EmptyState, Field, Loading, Menu, Notice, opensOnClick, PageHeader, Pill } from '../components/ui';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../components/table';
import { locale, t, tc, tp } from '../i18n';
import { formatNumber } from '../lib/calls';
import { useLoad, useSubmit } from '../lib/hooks';

// ---- The person's status ---------------------------------------------------------------

const statusNames: Record<AgentStatus, string> = { AVAILABLE: t('Available'), BREAK: t('On a break'), OFFLINE: t('Offline') };
const statusHints: Record<AgentStatus, string> = {
  AVAILABLE: t('Your calls and your queues ring you'),
  BREAK: t('Not rung; you keep your queue places'),
  OFFLINE: t('Not rung; out of your queues'),
};

/**
 * In the header: Available, On a break or Offline, and the person's queues
 * to join or leave. Takes effect on the next call.
 */
export function AgentStatusMenu({ up }: { up?: boolean }) {
  const [mine, setMine] = useState<MyQueues | null>(null);
  const [error, setError] = useState<string>();

  useEffect(() => {
    void api.myQueues().then(setMine, () => undefined);
  }, []);

  async function act(work: () => Promise<MyQueues>) {
    setError(undefined);
    try {
      setMine(await work());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (!mine) return null;
  return (
    <Menu
      label={t('Your status')}
      up={up ?? false}
      trigger={(props) => (
        <button type="button" className={`ghost status-button is-${mine.status.toLowerCase()}`} {...props} data-testid="agent-status" data-status={mine.status}>
          <span className="status-dot" aria-hidden /> <span className="hide-mobile">{statusNames[mine.status]}</span>
        </button>
      )}
    >
      {() => (
        <div className="status-menu">
          {(['AVAILABLE', 'BREAK', 'OFFLINE'] as const).map((status) => (
            <button
              key={status}
              type="button"
              role="menuitemradio"
              aria-checked={mine.status === status}
              className={`is-${status.toLowerCase()}`}
              onClick={() => void act(() => api.setAgentStatus(status))}
              data-testid={`status-${status.toLowerCase()}`}
            >
              <span className="status-dot" aria-hidden />
              <span className="grow">
                <strong>{statusNames[status]}</strong>
                <span className="subtle">{statusHints[status]}</span>
              </span>
              {mine.status === status && <Check size={16} aria-hidden />}
            </button>
          ))}
          {mine.queues.length > 0 && (
            <>
              <div className="menu-heading">{t('Your queues')}</div>
              {mine.queues.map((queue) => (
                <label key={queue.id} className="checkbox menu-check">
                  <input type="checkbox" checked={queue.joined} onChange={(e) => void act(() => api.setQueueJoined(queue.id, e.target.checked))} />
                  {queue.name}
                </label>
              ))}
            </>
          )}
          {error && <Notice kind="error">{error}</Notice>}
        </div>
      )}
    </Menu>
  );
}

// ---- Setting queues up --------------------------------------------------------------

function seconds(value: number | null): string {
  if (value == null) return t('no limit');
  return value % 60 === 0 && value >= 60 ? t('{n} min', { n: value / 60 }) : t('{n} s', { n: value });
}

/** Admins and region managers: the queues, their rules and their people. */
const peopleIn = (queue: QueueSummary) =>
  t('{n} of {total} in', { n: queue.members.filter((m) => m.joined && m.status !== 'OFFLINE').length, total: queue.members.length });

function queueColumns(): Column<QueueSummary>[] {
  return [
    {
      key: 'queue',
      label: t('Queue'),
      fixed: true,
      cell: (queue) => (
        <>
          <div className="name">{queue.name}</div>
          <div className="detail subtle">
            {t('{region} · rings {ring} s each · waits {wait}', { region: queue.regionName, ring: queue.ringSeconds, wait: seconds(queue.maxWaitSeconds) })}
          </div>
        </>
      ),
      excel: [
        { label: t('Queue'), value: (queue) => queue.name },
        { label: t('Region'), value: (queue) => queue.regionName },
      ],
    },
    {
      key: 'rule',
      label: t('Rule'),
      className: 'hide-mobile muted',
      cell: (queue) => t(queueStrategyNames[queue.strategy]),
      value: (queue) => t(queueStrategyNames[queue.strategy]),
    },
    { key: 'people', label: t('People'), cell: peopleIn, value: peopleIn },
    {
      key: 'numbers',
      label: t('Numbers'),
      className: 'hide-mobile muted tabular',
      cell: (queue) => queue.numbers.map((n) => formatNumber(n.e164)).join(', ') || '—',
      value: (queue) => queue.numbers.map((n) => n.e164).join(', '),
    },
    {
      key: 'members',
      label: t('Who is in it'),
      hidden: true,
      className: 'hide-mobile muted',
      cell: (queue) => queue.members.map((m) => m.name).join(', ') || '—',
      value: (queue) => queue.members.map((m) => m.name).join(', '),
    },
    {
      key: 'holdMusic',
      label: t('Hold music'),
      hidden: true,
      className: 'hide-mobile muted',
      cell: (queue) => queue.holdMusicName ?? t('The standard music'),
      value: (queue) => queue.holdMusicName ?? t('The standard music'),
    },
  ];
}

export function QueuesPage({ me }: { me: Me }) {
  const queues = useLoad(api.listQueues);
  const table = useTable('queues', queueColumns(), mayDownload(me.role) ? { name: t('Queues'), rows: () => queues.data ?? [] } : null);
  const options = useLoad(() => Promise.all([api.listRegions(), api.listUsers(), api.listHoldMusic()]));
  const [editing, setEditing] = useState<QueueSummary | 'new' | null>(null);
  const [error, setError] = useState<string>();
  const [regions, users, music] = options.data ?? [[], [], []];

  async function remove(queue: QueueSummary) {
    if (!confirm(t('Delete the queue {name}?', { name: queue.name }))) return;
    try {
      await api.deleteQueue(queue.id);
      queues.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="stack">
      <PageHeader title={t('Queues')} description={t('Callers wait in a queue, with hold music and their place in line, while its people are rung by the queue’s rule.')}>
        <Link to="/queues/live" className="button secondary">
          <Headset size={16} aria-hidden /> {t('Live view')}
        </Link>
        <button type="button" onClick={() => setEditing('new')} disabled={!options.data} data-testid="add-queue">
          <Plus size={16} aria-hidden /> {t('New queue')}
        </button>
      </PageHeader>
      {(error ?? queues.error ?? options.error) && <Notice kind="error">{error ?? queues.error ?? options.error}</Notice>}
      {!queues.data ? (
        <Loading />
      ) : queues.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={Users} title={t('No queues yet')}>
            <p>{t('Make a queue, add its people, then point a number at it on the Numbers page.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {queues.data.map((queue) => (
                <tr key={queue.id} data-testid="queue-row" {...opensOnClick(() => setEditing(queue))}>
                  <Cells table={table} row={queue} />
                  <td className="actions">
                    <Menu
                      label={t('Actions for {name}', { name: queue.name })}
                      trigger={(props) => (
                        <button type="button" className="ghost icon" aria-label={t('Actions for {name}', { name: queue.name })} {...props}>
                          <MoreHorizontal size={18} aria-hidden />
                        </button>
                      )}
                    >
                      {(close) => (
                        <>
                          <button type="button" role="menuitem" onClick={() => (close(), setEditing(queue))}>
                            <Pencil size={16} aria-hidden /> {t('Edit')}
                          </button>
                          <button type="button" role="menuitem" className="danger-item" onClick={() => (close(), void remove(queue))}>
                            <Trash2 size={16} aria-hidden /> {t('Delete')}
                          </button>
                        </>
                      )}
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('New queue') : editing ? t('Edit {name}', { name: editing.name }) : ''} wide>
        {editing && (
          <QueueForm
            queue={editing === 'new' ? null : editing}
            regions={me.role === 'TENANT_ADMIN' ? regions : regions.filter((r) => r.id === me.region?.id)}
            users={users}
            music={music}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              queues.reload();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function QueueForm({
  queue,
  regions,
  users,
  music,
  onCancel,
  onSaved,
}: {
  queue: QueueSummary | null;
  regions: RegionSummary[];
  users: UserSummary[];
  music: HoldMusicSummary[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState<QueueInput>(() =>
    queue
      ? { ...queue, members: queue.members.map((m) => ({ userId: m.userId, priority: m.priority })) }
      : {
          name: '',
          regionId: regions[0]?.id ?? '',
          strategy: 'ROUND_ROBIN',
          ringSeconds: 20,
          maxWaitSeconds: 300,
          wrapUpSeconds: 10,
          announcePosition: true,
          announceEverySeconds: 60,
          offerCallback: true,
          fallback: 'VOICEMAIL',
          longWaitAlertSeconds: 120,
          holdMusicId: null,
          members: [],
        },
  );
  const [adding, setAdding] = useState('');
  const form = useSubmit();
  const set = <K extends keyof QueueInput>(key: K, v: QueueInput[K]) => setValue((current) => ({ ...current, [key]: v }));
  const people = users.filter((u) => !u.disabled);
  const nameOf = (id: string) => people.find((u) => u.id === id)?.name ?? t('Someone');
  const candidates = people.filter((u) => !value.members.some((m) => m.userId === u.id));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const saved = await form.submit(queueSchema, value, (input) => (queue ? api.updateQueue(queue.id, input) : api.createQueue(input)));
    if (saved) onSaved();
  }

  const number = (key: 'ringSeconds' | 'wrapUpSeconds' | 'announceEverySeconds', label: string, hint: string) => (
    <Field label={label} hint={hint} error={form.fields[key]}>
      <input type="number" min={0} value={value[key]} onChange={(e) => set(key, Number(e.target.value))} />
    </Field>
  );

  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body">
        <div className="form-grid">
          <Field label={tc('thing', 'Name')} error={form.fields.name}>
            <input value={value.name} onChange={(e) => set('name', e.target.value)} autoFocus data-testid="queue-name" />
          </Field>
          <Field label={t('Region')} error={form.fields.regionId}>
            <select value={value.regionId} onChange={(e) => set('regionId', e.target.value)}>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Who is rung')} error={form.fields.strategy}>
            <select value={value.strategy} onChange={(e) => set('strategy', e.target.value as QueueInput['strategy'])}>
              {queueStrategies.map((s) => (
                <option key={s} value={s}>
                  {t(queueStrategyNames[s])}
                </option>
              ))}
            </select>
          </Field>
          {number('ringSeconds', t('Ring each person (seconds)'), t('Then the next is tried'))}
          <Field label={t('Longest wait')} hint={t('Then the queue’s fallback')} error={form.fields.maxWaitSeconds}>
            <select value={value.maxWaitSeconds ?? ''} onChange={(e) => set('maxWaitSeconds', e.target.value ? Number(e.target.value) : null)}>
              {[60, 120, 180, 300, 600, 900, 1800].map((s) => (
                <option key={s} value={s}>
                  {seconds(s)}
                </option>
              ))}
              <option value="">{t('No limit')}</option>
            </select>
          </Field>
          <Field label={t('When nobody answers')} error={form.fields.fallback}>
            <select value={value.fallback} onChange={(e) => set('fallback', e.target.value as QueueInput['fallback'])}>
              <option value="VOICEMAIL">{t('Take a message for the queue')}</option>
              <option value="HANGUP">{t('Hang up')}</option>
            </select>
          </Field>
          {number('wrapUpSeconds', t('Wrap-up after a call (seconds)'), t('Before the person is rung again'))}
          <Field label={t('Tell managers about a wait of')} error={form.fields.longWaitAlertSeconds}>
            <select value={value.longWaitAlertSeconds ?? ''} onChange={(e) => set('longWaitAlertSeconds', e.target.value ? Number(e.target.value) : null)}>
              {[60, 120, 180, 300, 600].map((s) => (
                <option key={s} value={s}>
                  {seconds(s)}
                </option>
              ))}
              <option value="">{t('Don’t tell them')}</option>
            </select>
          </Field>
          {number('announceEverySeconds', t('Tell callers their place every (seconds)'), '')}
          <Field label={t('Hold music')} hint={t('Your own on the Hold music page')} error={form.fields.holdMusicId}>
            <select value={value.holdMusicId ?? ''} onChange={(e) => set('holdMusicId', e.target.value || null)} data-testid="queue-hold-music">
              <option value="">{t('The standard music')}</option>
              {music
                .filter((m) => m.regionId === value.regionId)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.tracks.some((track) => track.status === 'READY') ? m.name : t('{name} (no tracks yet)', { name: m.name })}
                  </option>
                ))}
            </select>
          </Field>
        </div>
        <div className="queue-checks">
          <label className="checkbox">
            <input type="checkbox" checked={value.announcePosition} onChange={(e) => set('announcePosition', e.target.checked)} /> {t('Tell callers their place in line')}
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={value.offerCallback} onChange={(e) => set('offerCallback', e.target.checked)} /> {t('Callers may press 1 to keep their place and be called back')}
          </label>
        </div>

        <h3 className="section-title">{t('People')}</h3>
        <p className="subtle">{t('Priority 1 is rung first; people with a higher number when those before them are busy or away.')}</p>
        <ul className="plain-list member-list" data-testid="queue-members">
          {value.members.map((member, index) => (
            <li key={member.userId}>
              <span>{nameOf(member.userId)}</span>
              <select
                aria-label={t('Priority of {name}', { name: nameOf(member.userId) })}
                value={member.priority}
                onChange={(e) => set('members', value.members.map((m, i) => (i === index ? { ...m, priority: Number(e.target.value) } : m)))}
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>
                    {t('Priority {n}', { n: p })}
                  </option>
                ))}
              </select>
              <button type="button" className="ghost icon" aria-label={t('Remove {name}', { name: nameOf(member.userId) })} onClick={() => set('members', value.members.filter((_, i) => i !== index))}>
                <X size={16} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
        <div className="add-member">
          <select value={adding} onChange={(e) => setAdding(e.target.value)} aria-label={t('Person to add')} data-testid="queue-add-person">
            <option value="">{t('Add a person…')}</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {t('{name} (ext. {extension})', { name: u.name, extension: u.extension })}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="secondary"
            disabled={!adding}
            onClick={() => {
              set('members', [...value.members, { userId: adding, priority: 1 }]);
              setAdding('');
            }}
          >
            <Plus size={16} aria-hidden /> {t('Add')}
          </button>
        </div>
        {form.fields.members && <Notice kind="error">{form.fields.members}</Notice>}
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="save-queue">
          {queue ? t('Save changes') : t('Make queue')}
        </button>
      </div>
    </form>
  );
}

// ---- Live view ---------------------------------------------------------------------

const stateNames: Record<AgentLiveState, string> = {
  free: t('Free'),
  ringing: t('Ringing'),
  'on-call': t('On a call'),
  'wrap-up': t('Wrap-up'),
  break: t('On a break'),
  offline: t('Offline'),
  'not-joined': t('Left the queue'),
};
const stateKinds: Record<AgentLiveState, 'success' | 'warning' | 'danger' | 'brand' | undefined> = {
  free: 'success',
  ringing: 'brand',
  'on-call': 'danger',
  'wrap-up': 'warning',
  break: 'warning',
  offline: undefined,
  'not-joined': undefined,
};

function useTick(ms: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

function waited(since: string, now: number): string {
  const s = Math.max(0, Math.floor((now - Date.parse(since)) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Per queue, now: who is waiting and for how long, and where its people are. Pushed, not polled. */
export function QueuesLivePage({ canSetUp }: { canSetUp: boolean }) {
  const [queues, setQueues] = useState<QueueLive[] | null>(null);
  const [error, setError] = useState<string>();
  const now = useTick(1000);

  useEffect(() => {
    let stopped = false;
    void api.liveQueues().then(
      (list) => !stopped && setQueues(list),
      (err: unknown) => setError(err instanceof Error ? err.message : String(err)),
    );
    const stream = new EventSource(api.notificationStreamUrl());
    stream.addEventListener('queue', (event) => {
      const live = JSON.parse((event as MessageEvent<string>).data) as QueueLive;
      setQueues((list) => (list ? (list.some((q) => q.queueId === live.queueId) ? list.map((q) => (q.queueId === live.queueId ? live : q)) : [...list, live]) : list));
    });
    return () => {
      stopped = true;
      stream.close();
    };
  }, []);

  return (
    <div className="stack">
      <PageHeader title={t('Queues now')} description={t('Who is waiting and for how long, and who is free to answer. Updates as it happens.')}>
        {canSetUp && (
          <Link to="/queues" className="button secondary" data-testid="set-up-queues">
            <Pencil size={16} aria-hidden /> {t('Set up queues')}
          </Link>
        )}
      </PageHeader>
      {error && <Notice kind="error">{error}</Notice>}
      {!queues ? (
        <Loading />
      ) : queues.length === 0 ? (
        <div className="card">
          <EmptyState icon={Headset} title={t('No queues to show')}>
            <p>{t('Queues you are in, or manage, appear here.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="live-grid">
          {queues.map((queue) => (
            <section key={queue.queueId} className="card" data-testid="live-queue" data-queue-id={queue.queueId}>
              <div className="card-header">
                <h2>{queue.name}</h2>
                <span className="subtle tabular">
                  {t('Today {calls} · {answered} answered · {missed} missed · longest wait {wait}', {
                    calls: tp(queue.today.calls, '{n} call', '{n} calls'),
                    answered: queue.today.answered,
                    missed: queue.today.missed,
                    wait: waited(new Date(Date.now() - queue.today.longestWaitSeconds * 1000).toISOString(), Date.now()),
                  })}
                </span>
              </div>
              <div className="card-body live-columns">
                <div>
                  <h3 className="section-title">{t('Waiting ({n})', { n: queue.waiting.length })}</h3>
                  {queue.waiting.length === 0 ? (
                    <p className="subtle">{t('Nobody is waiting.')}</p>
                  ) : (
                    <ol className="plain-list live-waiting">
                      {queue.waiting.map((w) => (
                        <li key={`${w.position}-${w.caller}`} className="row">
                          <span className="tabular muted">{w.position}.</span>
                          <span className="grow tabular">{formatNumber(w.caller)}</span>
                          <span className="tabular">{waited(w.waitingSince, now)}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
                <div>
                  <h3 className="section-title">{t('People')}</h3>
                  <ul className="plain-list live-agents">
                    {queue.agents.map((a) => (
                      <li key={a.userId} className="row">
                        <span className="grow">{a.name}</span>
                        <Pill kind={stateKinds[a.state]} dot>
                          {stateNames[a.state]}
                        </Pill>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- The missed-call list ------------------------------------------------------------

/** "Today 14:05" or "12 Oct 14:05". */
function when(iso: string): string {
  const date = new Date(iso);
  const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  return date.toDateString() === new Date().toDateString() ? t('Today {time}', { time }) : `${date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} ${time}`;
}

const reasonNames: Record<CallbackSummary['reason'], string> = {
  ABANDONED: t('Hung up while waiting'),
  TIMEOUT: t('Waited too long'),
  NO_AGENTS: t('Nobody there'),
  REQUESTED: t('Asked to be called back'),
};

function callbackColumns(): Column<CallbackSummary>[] {
  return [
    {
      key: 'caller',
      label: t('Caller'),
      fixed: true,
      className: 'tabular',
      cell: (item) => (item.callId ? <Link to={`/calls/${item.callId}`}>{formatNumber(item.fromNumber)}</Link> : formatNumber(item.fromNumber)),
      value: (item) => item.fromNumber,
    },
    { key: 'queue', label: t('Queue'), className: 'hide-mobile muted', cell: (item) => item.queueName ?? '—', value: (item) => item.queueName },
    {
      key: 'why',
      label: t('Why'),
      cell: (item) => (
        <>
          {reasonNames[item.reason]}
          {item.state === 'WAITING' && (
            <Pill kind="brand" dot>
              {t('Place kept in the queue')}
            </Pill>
          )}
          {item.state === 'DONE' && item.doneByName && <div className="subtle">{t('Done by {name}', { name: item.doneByName })}</div>}
        </>
      ),
      excel: [
        { label: t('Why'), value: (item) => reasonNames[item.reason] },
        { label: t('Done'), value: (item) => (item.state === 'DONE' ? (item.doneByName ?? t('Done')) : null) },
      ],
    },
    { key: 'when', label: t('When'), className: 'hide-mobile muted', cell: (item) => when(item.createdAt), value: (item) => new Date(item.createdAt) },
  ];
}

/** Queue calls to call back, until someone marks them done. */
export function CallbacksPage({ me }: { me: Me }) {
  const [state, setState] = useState<'open' | 'done'>('open');
  const list = useLoad(() => api.listCallbacks({ state }), [state]);
  const table = useTable('callbacks', callbackColumns(), mayDownload(me.role) ? { name: t('Call back'), rows: () => list.data ?? [] } : null);
  const [error, setError] = useState<string>();
  const navigate = useNavigate();

  async function mark(item: CallbackSummary, done: boolean) {
    setError(undefined);
    try {
      await api.setCallbackDone(item.id, done);
      list.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="stack">
      <PageHeader title={t('Call back')} description={t('Queue callers nobody answered, and those who asked to be called back. Each stays here until someone marks it done.')}>
        <div className="segmented" role="group" aria-label={t('Show')}>
          {(['open', 'done'] as const).map((s) => (
            <button key={s} type="button" className={state === s ? 'active' : ''} aria-pressed={state === s} onClick={() => setState(s)}>
              {s === 'open' ? t('To call back') : t('Done')}
            </button>
          ))}
        </div>
      </PageHeader>
      {(error ?? list.error) && <Notice kind="error">{error ?? list.error}</Notice>}
      {!list.data ? (
        <Loading />
      ) : list.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={ListChecks} title={state === 'open' ? t('Nobody to call back') : t('Nothing done yet')}>
            {state === 'open' && <p>{t('Unanswered queue calls appear here.')}</p>}
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {list.data.map((item) => (
                <tr key={item.id} data-testid="callback-row">
                  <Cells table={table} row={item} />
                  <td className="actions">
                    <div className="row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                      {item.state !== 'DONE' ? (
                        <>
                          <button type="button" className="secondary small" onClick={() => void navigate(`/?number=${encodeURIComponent(item.fromNumber)}`)}>
                            <PhoneOutgoing size={15} aria-hidden /> {t('Call')}
                          </button>
                          <button type="button" className="small" onClick={() => void mark(item, true)} data-testid="callback-done">
                            <Check size={15} aria-hidden /> {t('Done')}
                          </button>
                        </>
                      ) : (
                        <button type="button" className="ghost small" onClick={() => void mark(item, false)}>
                          <RotateCcw size={15} aria-hidden /> {t('Reopen')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Queue reports (on the dashboard) -------------------------------------------------------

function minutes(s: number): string {
  return s >= 60 ? t('{m} min {s} s', { m: Math.floor(s / 60), s: s % 60 }) : t('{n} s', { n: s });
}

type QueueTotals = QueueReport['queues'][number];

function reportColumns(): Column<QueueTotals>[] {
  const time = (key: 'averageWaitSeconds' | 'longestWaitSeconds' | 'averageTalkSeconds', label: string): Column<QueueTotals> => ({
    key,
    label,
    className: 'num tabular hide-mobile',
    cell: ({ totals }) => minutes(totals[key]),
    // Seconds, for sums in Excel.
    excel: [{ label: `${label} (s)`, value: ({ totals }) => totals[key] }],
  });
  return [
    { key: 'queue', label: t('Queue'), fixed: true, cell: (row) => row.name, value: (row) => row.name },
    { key: 'calls', label: t('Calls'), className: 'num tabular', cell: ({ totals }) => totals.calls, value: ({ totals }) => totals.calls },
    {
      key: 'answered',
      label: tc('count', 'Answered'),
      className: 'num tabular',
      cell: ({ totals }) => (
        <>
          {totals.answered}
          {totals.calls > 0 && <span className="subtle"> ({Math.round((totals.answered / totals.calls) * 100)}%)</span>}
        </>
      ),
      value: ({ totals }) => totals.answered,
    },
    {
      key: 'missed',
      label: tc('count', 'Missed'),
      className: 'num tabular',
      cell: ({ totals }) => <span className={totals.missed ? 'danger-text' : undefined}>{totals.missed}</span>,
      value: ({ totals }) => totals.missed,
    },
    time('averageWaitSeconds', t('Average wait')),
    time('longestWaitSeconds', t('Longest wait')),
    time('averageTalkSeconds', t('Average talk')),
  ];
}

/** Per queue: calls, answered share, missed, average and longest wait, average talk time. */
export function QueueReportSection({ period, from, canDownload }: { period: 'day' | 'week'; from: string; canDownload: boolean }) {
  const report = useLoad<QueueReport>(() => api.queueReport({ period, from }), [period, from]);
  const table = useTable('queue-report', reportColumns(), canDownload ? { name: t('Queues'), rows: () => report.data?.queues ?? [] } : null);
  if (report.error || !report.data || report.data.queues.length === 0) return null;
  return (
    <div className="card table-wrap" data-testid="queue-report">
      <div className="card-header">
        <h2>{t('Queues')}</h2>
      </div>
      <table>
        <TableHead table={table} />
        <tbody>
          {report.data.queues.map((row) => (
            <tr key={row.queueId}>
              <Cells table={table} row={row} />
              <td className="actions" />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
